import { useTranslation } from 'react-i18next';
import { useGetSimpleHierarchyValuesQuery } from '@/api/generated/rest-v4-api';
import { CheckboxListFilter, type FilterOption } from '@/components/filters/checkbox-list-filter';
import { FilterGroup } from '@/components/filters/filter-group';
import { useHierarchyTypes } from '@/hooks/use-hierarchy-types';
import { captionText } from '@/utils/caption';

const HIERARCHY_VALUES_PAGE_SIZE = 1000;

interface HierarchySelectionProps {
  selectedValueIds: number[];
  onChange: (valueIds: number[]) => void;
}

const HierarchyTypeFilter = ({
  type,
  selectedValueIds,
  onChange,
}: HierarchySelectionProps & { type: FilterOption }) => {
  const { data, isLoading } = useGetSimpleHierarchyValuesQuery({
    filter: ['archived:eq:false', `typeId:eq:${type.id}`],
    sort: ['caption'],
    pageSize: HIERARCHY_VALUES_PAGE_SIZE,
  });

  const options = (data?.items ?? []).flatMap((value) =>
    value.id === undefined ? [] : [{ id: value.id, label: captionText(value.caption) }]
  );

  return (
    <CheckboxListFilter
      label={type.label}
      options={options}
      selectedIds={selectedValueIds}
      onChange={onChange}
      isLoading={isLoading}
    />
  );
};

export const HierarchyFilter = ({ selectedValueIds, onChange }: HierarchySelectionProps) => {
  const { t } = useTranslation();
  const { hierarchyTypes, isLoading } = useHierarchyTypes();

  return (
    <FilterGroup
      title={t(($) => $.hierarchy)}
      items={hierarchyTypes}
      isLoading={isLoading}
      showAllLabel={t(($) => $.showAllHierarchies)}
      showLessLabel={t(($) => $.showLessHierarchies)}
    >
      {(type) => (
        <HierarchyTypeFilter key={type.id} type={type} selectedValueIds={selectedValueIds} onChange={onChange} />
      )}
    </FilterGroup>
  );
};
